import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { translations } from "./translations";

const LanguageContext = createContext({
    language: "en",
    setLanguage: () => {},
    t: (key) => key
});

const SUPPORTED_LANGUAGES = ["en", "it", "de"];
const STORAGE_KEY = "app_language";

const normalizeLanguage = (language) => {
    if (!language) {
        return "en";
    }
    const normalized = language.toLowerCase().split("-")[0];
    return SUPPORTED_LANGUAGES.includes(normalized) ? normalized : "en";
};

const getInitialLanguage = () => {
    if (typeof window === "undefined") {
        return "en";
    }
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored) {
        return normalizeLanguage(stored);
    }
    return normalizeLanguage(window.navigator.language);
};

const getValueByPath = (obj, path) => {
    if (!obj || !path) {
        return undefined;
    }
    return path.split(".").reduce((current, key) => {
        if (current && Object.prototype.hasOwnProperty.call(current, key)) {
            return current[key];
        }
        return undefined;
    }, obj);
};

export const LanguageProvider = ({ children }) => {
    const [language, setLanguageState] = useState(getInitialLanguage);

    const setLanguage = (nextLanguage) => {
        const normalized = normalizeLanguage(nextLanguage);
        setLanguageState(normalized);
        if (typeof window !== "undefined") {
            window.localStorage.setItem(STORAGE_KEY, normalized);
        }
    };

    useEffect(() => {
        if (typeof document !== "undefined") {
            document.documentElement.lang = language;
        }
    }, [language]);

    const t = (key, fallback) => {
        const value = getValueByPath(translations[language], key);
        if (value !== undefined) {
            return value;
        }
        const fallbackValue = getValueByPath(translations.en, key);
        if (fallbackValue !== undefined) {
            return fallbackValue;
        }
        return fallback || key;
    };

    const contextValue = useMemo(
        () => ({
            language,
            setLanguage,
            t
        }),
        [language]
    );

    return (
        <LanguageContext.Provider value={contextValue}>
            {children}
        </LanguageContext.Provider>
    );
};

export const useTranslation = () => useContext(LanguageContext);

export const LanguageSetter = ({ language, children }) => {
    const { setLanguage } = useTranslation();

    useEffect(() => {
        if (language) {
            setLanguage(language);
        }
    }, [language, setLanguage]);

    return <>{children}</>;
};

export const SUPPORTED_LANGUAGE_LIST = SUPPORTED_LANGUAGES;
